<script>
    import {_, number} from 'svelte-i18n';
    import '../../styles/atlases/GameItems.css';
    import '../../styles/atlases/HeroEquipment.css';
    import '../../styles/atlases/VanityFrames.css';
    import '../../styles/atlases/CommandersElementsItems.css';
    import { tippyAction as tippy } from '../actions/tippy.js';
    import { autoScrollY } from '../actions/autoScrollY.js';
    import { autoScrollX } from '../actions/autoScrollX.js';
    import 'tippy.js/dist/tippy.css'; // optional
    import 'tippy.js/animations/shift-away-subtle.css';
    import {itemRarityLowercase} from '../../constants';
    import greenCheckmark from '../../assets/gold_rush/green_checkmark.png?format=webp;jpg';
    import temporaryBorder from '../../assets/temporary_item_border.png?format=webp;jpg';
    import {gameConfig, isMobile} from "../../stores.js";

    let { itemDetails } = $props();

    let greenCheckmarkImage = greenCheckmark;
    let temporaryBorderImage = temporaryBorder;

    const titleAttribute = {
        name: 'titleAttribute',
        defaultValue: true,
        fn() {
            return {
                onCreate(instance) {
                    if (!instance.props.titleAttribute) {
                        return;
                    }

                    const title = instance.reference.getAttribute('title');

                    if (title) {
                        instance.setContent(title);
                        instance.reference.removeAttribute('title');
                    }
                },
            };
        },
    };

    // Function to calculate time left in days and hours
    function formatTimeLeft(expireAt) {
        const now = new Date();
        const expirationDate = new Date(expireAt);
        const timeDifference = expirationDate - now;

        if (timeDifference <= 0) {
            return $_('Expired');
        }

        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));

        return `${days}d ${hours}h ${minutes}m`;
    }

    // Long formatted amounts ("30,000") shrink to keep the mobile amount column
    // at its fixed width — otherwise they push the icon right and out of line
    // with the other rows. Tiers are by rendered (locale-formatted) length.
    function amountTierClass(formatted) {
        if (formatted.length >= 6) return 'amt-xs';
        if (formatted.length === 5) return 'amt-sm';
        return '';
    }

    // Function to generate tooltip content
    function generateTooltipContent(details) {
        // Replace newline characters with <br /> tags for proper multi-line formatting
        const formattedDescription = (details.description || '').replace(/\n/g, '<br />');

        return details.expireAt
            ? `${formattedDescription}
<br /><br /><span style="display: flex; gap: 3px;">
<img src="/images/timer_icon_with_outline.png" width="14px" height="16px" alt="Timer Icon">
${$_('ExpiresIn')}: ${formatTimeLeft(details.expireAt)}
</span>`
            : formattedDescription;
    }
</script>

<div
        alt={itemDetails.icon}
        class="item"
        use:tippy={{
    content: generateTooltipContent(itemDetails),
    allowHTML: true,
    theme: 'shop',
    boundary: 'scrollingContainer',
    arrow: true,
    flip: false,
    placement: 'top-start',
    animation: 'shift-away-subtle',
    plugins: [titleAttribute],
    delay: 500,
  }}
>
    <div class="item-iconbox">
        <div
                class="
        item-icon
        {itemDetails.rarityClass ?? itemRarityLowercase[itemDetails.rarity]}
        {itemDetails.icon}
        "
        >
            {#if itemDetails.commanderTalentBackground}
                <img src="/images/medals/{itemDetails.commanderTalentBackground}" alt=""/>
            {/if}
        </div>
        <!-- Frames decorate the icon, so they live in the icon's positioning box:
             they stay centred on it regardless of row layout (mobile reorders the
             amount column before the icon) and of the icon size in each context. -->
        {#if itemDetails.expireAt}
            <picture class="temporaryBackground">
                <source srcset={temporaryBorderImage[0]} type="image/webp">
                <source srcset={temporaryBorderImage[1]} type="image/jpg">
                <img alt="Temporary Border" src={temporaryBorderImage[0]}>
            </picture>
        {/if}
        {#if itemDetails.frameImage}
            <picture class="temporaryBackground">
                <source srcset={itemDetails.frameImage[0]} type="image/webp">
                <source srcset={itemDetails.frameImage[1]} type="image/jpg">
                <img alt="Item Frame" src={itemDetails.frameImage[0]}>
            </picture>
        {/if}
    </div>

    {#if itemDetails.claimed}
        <picture class="greenCheckmark">
            <source srcset={greenCheckmarkImage[0]} type="image/webp">
            <source srcset={greenCheckmarkImage[1]} type="image/jpg">
            <img alt="Green Checkmark" src={greenCheckmarkImage[0]}>
        </picture>
    {/if}
    <p
            class="item-title"
            use:autoScrollY={{ group: 'items' }}
            use:autoScrollX={{ group: 'items-x', enabled: $isMobile }}
    >{itemDetails.title}</p>
    <strong class="item-amount {amountTierClass($number(itemDetails.amount))}">{$number(itemDetails.amount)}</strong>
</div>

<style lang="scss">
  @use '../../styles/breakpoints.scss' as bp;

  .item {
    font-size: 0.75em;
    color: var(--color-darkGrey);
    height: 47px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding: 3px 5px;
    display: flex;
    overflow: hidden;
    clear: both;
    position: relative;
    cursor: default;
    width: 100%;
    text-shadow: 0 1px 0px rgba(255, 255, 255, 0.7);
    justify-content: space-between;
    align-items: center;

    &:last-child {
      border: none;
    }

    &:hover {
      .greenCheckmark {
        transform: translateZ(0) scale(1.2);
      }
    }

    .greenCheckmark {
      position: absolute;
      bottom: 5px;
      right: 5px;
      z-index: 2;
      pointer-events: none;
      transition: transform 0.15s ease-in;
      backface-visibility: hidden;
      transform: translateZ(0) scale(1);
    }

    .item-iconbox {
      position: relative;
      flex-shrink: 0;
    }

    .temporaryBackground {
      position: absolute;
      inset: 0;
      z-index: 2;
      pointer-events: none;
      backface-visibility: hidden;
      transform: translateZ(0);

      img {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    .item-icon {
      width: 40px;
      height: 40px;
      display: flex;
      max-width: 40px;
      max-height: 40px;
      overflow: hidden;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      position: relative;

      & > img {
        width: 100%;
        position: absolute;
      }

      &:hover {
        &::after {
          transform: translateZ(0) scale(1);
        }
      }

      &[class*='commanderTalent'] {
        transition: transform 0.15s ease-in;

        &:after {
          transform: scale(0.7) !important;
          margin-left: 0px;
          left: -3px;
          position: relative;
        }

        &:hover {
          transform: scale(1.3);
        }
      }

      &[class*='choiceChest'] {
        &:after {
          right: -3px;
          position: relative;
        }
      }

      &[class*='chat-avatar-commander'] {
        &:after {
          border-radius: 10px;
        }
      }

      &:after {
        // The atlas CSS sizes a sprite by this frame's width and derives its height
        // from the sprite's aspect ratio through a percentage padding-bottom, which
        // resolves against the frame's width. A taller-than-wide canvas (Fowler's
        // Solution is 1:2) therefore stands taller than the square frame and gets
        // clipped — and narrowing it alone would not lower it. postcss-atlas-sprite-fit
        // annotates every non-square sprite with --sprite-ratio (height / width), so
        // contain-fit it: cap the height at the frame and narrow the width by the
        // same factor. Square (unannotated) and wide sprites resolve to the atlas's
        // own values.
        width: calc(100% * min(1, 1 / var(--sprite-ratio, 1)));
        padding-bottom: calc(100% * min(1, var(--sprite-ratio, 1)));
        transition: transform 0.15s ease-in;
        backface-visibility: hidden;
        transform: translateZ(0) scale(0.8);
        image-rendering: crisp-edges;
      }

      &.default {
        background-image: url('/images/item_rarity_plate_default.png');
        background-size: cover;
      }

      &.common {
        background-image: url('/images/item_rarity_plate_common.png');
        background-size: cover;
      }

      &.rare {
        background-image: url('/images/item_rarity_plate_rare.png');
        background-size: cover;
      }

      &.epic {
        background-image: url('/images/item_rarity_plate_epic.png');
        background-size: cover;
      }

      &.legendary {
        background-image: url('/images/item_rarity_plate_legendary.png');
        background-size: cover;
      }
    }

    strong {
      height: 100%;
      text-align: right;
      font-size: 1.2em;
      font-weight: bold;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    p {
      height: 100%;
      text-align: left;
      padding-left: 10px;
      padding-right: 10px;
      display: flex;
      align-items: center;
      flex-grow: 1;
      justify-content: flex-start;
      line-height: 1em;
      overflow: hidden;
      min-width: 0;
    }

    // Mobile: keep titles on a single line so long names overflow horizontally
    // and the autoScrollX marquee kicks in (desktop keeps wrapping + autoScrollY).
    // Also lead with the quantity as "Nx" before the icon (e.g. "1x ⚔ Sword") so
    // the count reads as a multiplier of the item instead of a lone number floating
    // on the right. Reordered via flex `order` so the desktop DOM/render is untouched.
    @include bp.mobile {
      justify-content: flex-start;

      p.item-title {
        white-space: nowrap;
        order: 1;
      }

      .item-iconbox {
        order: 0;
      }

      .item-amount {
        order: -1;
        flex-shrink: 0;
        // Fixed-width, right-aligned amount column so every icon starts at the same
        // x regardless of the number's digits ("1x", "12x", "999x" all align).
        // Long amounts shrink (amt-sm/amt-xs) instead of widening the column. The
        // min-width em resolves against the already-scaled own font-size, so
        // dividing by the scale keeps the physical column constant ("1x" and
        // "30,000x" align identically). Contexts that enlarge amounts (e.g. the
        // pack detail sheet) set --amt-base.
        --amt-scale: 1;
        font-size: calc(var(--amt-base, 1.2em) * var(--amt-scale));
        min-width: calc(2.4em / var(--amt-scale));
        text-align: right;
        margin-right: 8px;

        &::after {
          content: 'x';
        }

        &.amt-sm {
          --amt-scale: 0.8;
        }

        &.amt-xs {
          --amt-scale: 0.65;
        }
      }

      // Fill the (possibly enlarged) icon box instead of capping at the sprite's
      // natural max size. The atlas caps max-width but the padding-based height
      // isn't capped, so a small sprite in a bigger box (e.g. the 62px gold_l coin
      // inside the 72px modal icon) got stretched to the box height → squished.
      .item-icon::after {
        max-width: 100% !important;
        max-height: 100% !important;
      }
    }
  }
</style>
